import Vue from "vue";
import { mapGetters } from "vuex";

import { oc } from "ts-optchain";

import { ErrorLogService, EventTrackingService, ObjectUtil, PartDetailServices } from "@/services";

import { LoadingSpinner, StaticFormControl } from "@/components";
import { formatDateTime, stringHelper } from "@/components/mixins";

import { IFullPartDetailWithStatusDto, MessageStatusType, PartValidationReason, SalesOrganization, ImageCopyright } from "@/types";

import { default as ImageGallery } from "./ImageGallery/ImageGallery.vue";
import { default as Error } from "./Status/Error.vue";
import { default as PartNotFound } from "./Status/PartNotFound.vue";
import { default as PartStatusMessage } from "./PartStatusMessage/PartStatusMessage.vue";

// Dialogs
import { default as DetailsAndPricing } from "./DetailsAndPricing/DetailsAndPricing.vue";
import { default as AddPartToList } from "./Dialogs/AddPartToList/AddPartToList.vue";
import { default as EmailPartImages } from "./Dialogs/EmailPartImages/EmailPartImages.vue";
import { default as PrintPartImages } from "./Dialogs/PrintPartImages/PrintPartImages.vue";
import { default as RequestCurrentPricing } from "./Dialogs/RequestCurrentPricing/RequestCurrentPricing.vue";

// Tabs
import { default as ApplicationsTab } from "./Tabs/Applications/ApplicationsTab.vue";
import { default as ComponentsTab } from "./Tabs/Components/ComponentsTab.vue";
import { default as CoreDeductionsTab } from "./Tabs/CoreDeductions/CoreDeductionsTab.vue";
import { default as CrossReferenceTab } from "./Tabs/CrossReference/CrossReferenceTab.vue";
import { default as GeneralTab } from "./Tabs/General/GeneralTab.vue";
import { default as OrderHistoryTab } from "./Tabs/OrderHistory/OrderHistoryTab.vue";
import { default as RelatedItemsTab } from "./Tabs/RelatedItems/RelatedItemsTab.vue";
import { default as SupersessionTab } from "./Tabs/Supersession/SupersessionTab.vue";

export default Vue.extend({
  name: "PartDetails",

  components: {
    AddPartToList,
    ApplicationsTab,
    ComponentsTab,
    CoreDeductionsTab,
    CrossReferenceTab,
    DetailsAndPricing,
    EmailPartImages,
    PrintPartImages,
    Error,
    GeneralTab,
    ImageGallery,
    LoadingSpinner,
    OrderHistoryTab,
    PartNotFound,
    RelatedItemsTab,
    RequestCurrentPricing,
    StaticFormControl,
    SupersessionTab,
    PartStatusMessage
  },
  mixins: [formatDateTime, stringHelper],

  props: {
    oemPartCode: {
      type: String,
      default: "",
    },
    partId: {
      default: null,
      validator: (value) => {
        if (value) {
          const num = Number(value);
          return !isNaN(num);
        }
        return true;
      },
    },
  },

  data: () => ({
    // Flags
    isBusy: false,
    activeView: "DEFAULT",
    errorMessage: null,
    highCostWarningMinimum: 6499,

    // State
    details: null,
    partListName: null,
    partListId: null,
    requestReason: "",
    alertMessage: "",
    alertVariant: "info",
    areImagesLoading: true,
    haveShownInitialTab:  false,
    showTabs: true 
  }),

  computed: {
    ...mapGetters(["activeAccountId", "audienceIds", "getContentItem", "hasPermission", "user", "IsMcfa"]),
    stockFacingWarehouse(){
      let warehouseQuantities = this.details.WarehouseStockQuantities;

      if(!warehouseQuantities){
        this.isLoadingFacingWarehouse = false;  
        return;
      }
    
      if(this.facingWarehouse.length > 0){
        let stockFacingWarehouse = warehouseQuantities.find(x => {
          return x.WarehouseCode == this.facingWarehouse[0]; // first warehouse should be stock facing, the rest are alternates
        })

        if(stockFacingWarehouse){
          this.facingWarehouseCode = stockFacingWarehouse.WarehouseCode;
          this.facingWarehouseQuantity = stockFacingWarehouse.Quantity;
        }
      }
      this.isLoadingFacingWarehouse = false;      
    },
  },
  created() {
    this.getPartDetail();
  },
  mounted () {
    window.scrollTo(0, 0)
  },
  updated(){
    
    if(!this.haveShownInitialTab && this.$route.query.showTab){
      this.haveShownInitialTab = true;
      this.showInitialTab();
      this.scrollToTabs();
    }
  },

  methods: {
    async getPartDetail() {
      try {
        this.isBusy = true;

        const params = {
          oemPartCode: this.oemPartCode,
          partId: this.partId,
          accountId: this.activeAccountId,
          userId: this.user.UserId,
          audienceIds: this.audienceIds,
        };

        const data = await PartDetailServices.getPartDetail(params);
        
        if (data === undefined || data.Validation === null || data.PartNullForUserCostCenter === true) {
          data.PartStatusMessage = {
            ComponentPartsCount: 0,
            IsOrderable: false,
            LongMessage: "ItemNotPricedLong",
            MessageStatusType: MessageStatusType.Error,
            PartValidationReason: PartValidationReason.ItemNotPriced,
            ReplacementPartId: 0,
            ReplacementPartsCount: 0,
            ShortMessage: "ItemNotPriced",
            ReplacementPartOemPartCode: null
          };
          this.showTabs = false;
        }
        
        this.details = data;
        
        this.details.WeightUnitLocalizationKey = this.IsMcfa ? "Lbs_period" : "Kilograms";
        this.DiscountPrice = this.getDiscountPrice(this.details);
        if( this.details.IsSpecialOrder){
          this.details.SpecialOrderWarehouse = "Special Order"
        } else{
          this.details.SpecialOrderWarehouse = "Drop Shipped"
        }
        this.setTabVisibility();   
        this.getPartImages();

      } catch (err) {
        this.errorMessage = ErrorLogService.LogAndGetErrorMessage(err);

        this.activeView =
          this.errorMessage === "Not Found" || oc(err as any).reponse.status() === 404 ? "NOT_FOUND" : "ERROR";
      } finally {
        this.isBusy = false;
      }
    },
    getDiscountPrice(partDetail: IFullPartDetailWithStatusDto){
      const discounts = oc(partDetail).PromoDiscounts([]);

      const originalPrice = partDetail.DealerNetPrice;

      const promoDiscount = oc(discounts.filter((x) => x.OriginalPricePerUnit === originalPrice))[0]();

      if (promoDiscount && promoDiscount.Discount) {
          return originalPrice - promoDiscount.Discount;
      }

      return null;
    },
    setTabVisibility(){
      if(this.details.OemPartCode === 'BARNESPROGRAM' || this.details.OemPartCode === "PROMATCHHARDWARE"){
        this.details.ShowCrossReferencesTab = false;
      }
      else{
        this.details.ShowCrossReferencesTab = true;
      }  

      const rpiSalesOrg = "RP";
      this.details.ShowApplicationsTab = this.details.ProvisionCode === rpiSalesOrg; 
      this.details.ShowComponentsTab = this.details.ProvisionCode !== rpiSalesOrg;
      this.details.ShowSupersessionsTab = this.details.IsSuperseeed || this.details.SalesOrganization === rpiSalesOrg;
      this.details.ShowCoreDeductionsTab = this.details.CoreDeductions && this.details.CoreDeductions.length > 0;
      this.details.ShowRelatedPartsTab = this.details.RelatedParts && this.details.RelatedParts.length > 0;
    },
    async getPartImages(){
      const params = {
        oemId: this.details.OemId ? this.details.OemId : "",
        height: 300,
        width: 300,
        copyRight: this.getImageCopyRight(this.details.SalesOrganization),
        getPublicImagesOnly: "True",
        partId: this.details.Id
      };      

      const imagesData = await PartDetailServices.getPartImages(params)
      this.details.Images = imagesData;
      this.areImagesLoading = false;
    },
    getImageCopyRight(salesOrganization: SalesOrganization){
      return salesOrganization === SalesOrganization.RP ? ImageCopyright.Rapidparts : ImageCopyright.Mcfa;
    },
    addToPartList() {
      this.showAddToPartList = true;
    },

    emailPartImages() {
      this.activeView = "EMAIL_PART_IMAGES";
    },

    printPartImages() {
      EventTrackingService.trackEvent({
        category: "Part Detail",
        action: "Print Images",
        label: oc(this.details as IFullPartDetailWithStatusDto).OemPartCode(""),
        value: oc(this.details as IFullPartDetailWithStatusDto).Images([]).length,
      });

      this.activeView = "PRINT_PART_IMAGES";
    },

    closeForm() {
      this.requestReason = "";
      this.activeView = "DEFAULT";
    },

    showInitialTab(){
      var showTab = this.$route.query.showTab;
      if(showTab === 'supersession'){
        this.clickSupersessionTab();       
      }
    },

    scrollToTabs(){
      var tabs = document.getElementById("js-part-detail-tabs");
      if(tabs){
        tabs.scrollIntoView()
      }
    },

    // START tab click event handlers

    clickGeneralTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#generalTab`);
    },
    
    clickCrossReferenceTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#crossReferenceTab`);
      this.$refs.crossReferenceTabContent.loadTabData();
    },

    clickApplicationsTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#applicationsTab`);
      this.$refs.applicationsTabContent.loadTabData();
    },

    clickComponentsTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#componentsTab`);
      this.$refs.componentsTabContent.loadTabData();
    },

    clickCoreDeductionsTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#coreDeductionsTab`);
      this.$refs.coreDeductions.loadTabData();
    },

    clickSupersessionTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#supersessionTab`);
      this.$refs.supersessionTabContent.loadTabData();
    },

    clickOrderHistoryTab(){
      EventTrackingService.trackPageView(`${this.$route.fullPath}#orderHistoryTab`);
      this.$refs.orderHistoryTabContent.loadTabData();
    },
    // END tab click event handlers
  },
});